import type { StaffCustomerSummary } from "@lafayette/schemas";

import { deepLinks } from "../navigation/deep-links";
import type { AdminArea } from "../navigation/offering";

// What the search dialog (⌘K) lists for a query: the pages the staff member is offered, and the customers they may open. Matching is a
// case-insensitive "contains": every word of the query must be somewhere in the label.

export type PaletteResult = Readonly<{
  /** Stable key; also the id of the option. */
  key: string;
  group: "page" | "customer";
  label: string;
  to: string;
  /** Pages only: the key of the area, for its icon. */
  area?: AdminArea["key"];
}>;

/** Customers listed at most, so a short query on a long list stays readable. */
export const PALETTE_CUSTOMER_LIMIT = 6;

const UNNAMED_CUSTOMER = "Name not set";

const words = (text: string): readonly string[] =>
  text.toLocaleLowerCase().split(/\s+/u).filter(Boolean);

function matches(label: string, query: readonly string[]): boolean {
  const haystack = label.toLocaleLowerCase();
  return query.every((word) => haystack.includes(word));
}

/**
 * The results for a query, pages first. An empty query lists every page and no customer (a list of names is shown only to a person who
 * types). `customers` is `undefined` while the list is not read, or not offered to this role.
 */
export function paletteResults(
  query: string,
  areas: readonly AdminArea[],
  customers: readonly StaffCustomerSummary[] | undefined,
): readonly PaletteResult[] {
  const terms = words(query);
  const pages = areas
    .filter((area) => matches(area.label, terms))
    .map(
      (area): PaletteResult => ({
        key: `page-${area.key}`,
        group: "page",
        label: area.label,
        to: area.path,
        area: area.key,
      }),
    );
  if (terms.length === 0 || customers === undefined) return pages;
  const found = customers
    .filter((customer) => matches(customer.preferredName ?? UNNAMED_CUSTOMER, terms))
    .slice(0, PALETTE_CUSTOMER_LIMIT)
    .map(
      (customer): PaletteResult => ({
        key: `customer-${customer.customerId}`,
        group: "customer",
        label: customer.preferredName ?? UNNAMED_CUSTOMER,
        to: deepLinks.customerSupport(customer.customerId),
      }),
    );
  return [...pages, ...found];
}
